import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  AudiencePersonaAssociationRequest,
  AudiencePersonaAssociationWithPersonaDto,
  LayoutDataDto,
  PersonaAudienceAssociationSummaryDto,
  PersonaImageUploadDto,
  PersonaInputDto,
  PersonaOutputDto,
  PersonaSummaryDto,
} from '@kortxio/hub-api';
import api, { axiosInstance } from 'libs/api';
import { parseApiPayload, toApiError } from 'libs/api/helper';
import type { ApiError } from 'libs/api/types';

export type PersonaEntity = {
  id: string;
  name: string;
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
  data: LayoutDataDto;
};

export type PersonaSummary = {
  id: string;
  personaName: string;
  updatedAt: string;
  audienceAssociations?: PersonaAudienceAssociationSummaryDto[];
};

type AudienceContext = {
  clientId: number;
  advertiserId: number;
  audienceId: string;
};

const personasApiInternal = createApi({
  reducerPath: 'personasApi',
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ['AudiencePersonaAssociation', 'Personas', 'Persona'],
  refetchOnMountOrArgChange: true,
  endpoints: (build) => ({
    listPersonas: build.query<PersonaSummary[], void>({
      queryFn: async () => {
        try {
          const response = await api.persona.listPersonas();
          const payload =
            (parseApiPayload(response) as PersonaSummaryDto[] | undefined) ??
            [];
          const data = payload.map(mapPersonaSummaryDtoToPersonaSummary);

          return { data };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Persona' as const, id })),
              { type: 'Personas', id: 'LIST' },
            ]
          : [{ type: 'Personas', id: 'LIST' }],
    }),
    getPersona: build.query<PersonaEntity, string>({
      queryFn: async (id) => {
        try {
          const response = await api.persona.getPersonaById(id);
          const payload = parseApiPayload(response) as
            | PersonaOutputDto
            | undefined;

          if (!payload) {
            return {
              error: toApiError(new Error(`Persona with id ${id} not found.`)),
            };
          }

          return { data: mapPersonaDtoToPersonaEntity(payload) };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      providesTags: (_result, _error, id) => [{ type: 'Persona', id }],
    }),
    createPersona: build.mutation<PersonaEntity, PersonaInputDto>({
      queryFn: async (data) => {
        try {
          const response = await api.persona.createPersona(data);
          const persona = parseApiPayload(response) as
            | PersonaOutputDto
            | undefined;

          if (!persona) {
            return {
              error: toApiError(
                new Error('Unexpected empty response when creating persona.')
              ),
            };
          }

          return { data: mapPersonaDtoToPersonaEntity(persona) };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      invalidatesTags: [{ type: 'Personas', id: 'LIST' }],
    }),
    updatePersona: build.mutation<
      PersonaEntity,
      { id: string; data: PersonaInputDto }
    >({
      queryFn: async ({ id, data }) => {
        try {
          const response = await api.persona.updatePersona(id, data);
          const persona = parseApiPayload(response) as
            | PersonaOutputDto
            | undefined;

          if (!persona) {
            return {
              error: toApiError(
                new Error('Unexpected empty response when updating persona.')
              ),
            };
          }

          return { data: mapPersonaDtoToPersonaEntity(persona) };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      invalidatesTags: (_result, error, arg) => {
        if (error) {
          return [];
        }

        return [
          { type: 'Personas', id: 'LIST' },
          { type: 'Persona', id: arg.id },
          {
            type: 'AudiencePersonaAssociation',
            id: arg.id,
          },
        ];
      },
    }),
    uploadPersonaImage: build.mutation<string, File>({
      queryFn: async (file) => {
        try {
          const response = await api.persona.uploadPersonaImage(file);
          const payload = parseApiPayload(response) as
            | PersonaImageUploadDto
            | undefined;
          const imageUrl = payload?.url?.trim();

          if (!imageUrl) {
            return {
              error: toApiError(
                new Error(
                  'Unexpected empty response when uploading persona image.'
                )
              ),
            };
          }

          return { data: imageUrl };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
    }),
    deletePersona: build.mutation<void, string>({
      queryFn: async (id) => {
        try {
          const personaApi = api.persona;

          if (typeof personaApi.deletePersona === 'function') {
            await personaApi.deletePersona(id);
          } else {
            await axiosInstance.delete(`/personas/${id}`);
          }

          return { data: undefined };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      invalidatesTags: (_result, error, id) => {
        if (error) {
          return [];
        }

        return [
          { type: 'Personas', id: 'LIST' },
          { type: 'Persona', id },
          {
            type: 'AudiencePersonaAssociation',
            id: id,
          },
        ];
      },
    }),
    getAudiencePersonaAssociation: build.query<
      AudiencePersonaAssociationWithPersonaDto | null,
      AudienceContext
    >({
      async queryFn(args) {
        const { clientId, advertiserId, audienceId } = args;

        try {
          const response = await api.audiences.getAudiencePersonaAssociation(
            clientId,
            advertiserId,
            audienceId
          );

          const payload = parseApiPayload(response) as
            | AudiencePersonaAssociationWithPersonaDto
            | undefined;

          if (!payload) {
            return { data: null };
          }

          return {
            data: {
              personaId: payload.personaId,
              persona: payload.persona,
            },
          };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      providesTags: (result, error, args) => {
        if (error) {
          return [];
        }

        if (result?.persona === undefined) {
          return [];
        }

        return [
          {
            type: 'AudiencePersonaAssociation',
            id: `${args.audienceId}-${result.persona.id}`,
          },
          {
            type: 'AudiencePersonaAssociation',
            id: result.persona.id,
          },
        ];
      },
    }),
    upsertAudiencePersonaAssociation: build.mutation<
      void,
      AudienceContext & { personaId: string }
    >({
      async queryFn(args) {
        const { clientId, advertiserId, audienceId, personaId } = args;

        try {
          const payload: AudiencePersonaAssociationRequest = { personaId };
          await api.audiences.createOrUpdateAudiencePersonaAssociation(
            clientId,
            advertiserId,
            audienceId,
            payload
          );

          return { data: undefined };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      invalidatesTags: (_result, error, args) => {
        if (error) {
          return [];
        }

        return [
          {
            type: 'AudiencePersonaAssociation',
            id: `${args.audienceId}-${args.personaId}`,
          },
          {
            type: 'AudiencePersonaAssociation',
            id: args.personaId,
          },
          { type: 'Personas', id: 'LIST' },
          { type: 'Persona', id: args.personaId },
        ];
      },
    }),
  }),
});

export const personasApi = personasApiInternal;

export const {
  useListPersonasQuery,
  useLazyListPersonasQuery,
  useGetPersonaQuery,
  useCreatePersonaMutation,
  useUpdatePersonaMutation,
  useUploadPersonaImageMutation,
  useDeletePersonaMutation,
  useGetAudiencePersonaAssociationQuery,
  useUpsertAudiencePersonaAssociationMutation,
} = personasApi;

function mapPersonaSummaryDtoToPersonaSummary(
  dto: PersonaSummaryDto
): PersonaSummary {
  return {
    id: dto.id ?? '',
    personaName: dto.name ?? '',
    updatedAt: dto.updatedAt ?? '',
    audienceAssociations: dto.audienceAssociations,
  };
}

function mapPersonaDtoToPersonaEntity(dto: PersonaOutputDto): PersonaEntity {
  if (
    dto.id == null ||
    dto.name == null ||
    dto.schemaVersion == null ||
    dto.createdAt == null ||
    dto.updatedAt == null ||
    dto.data == null
  ) {
    throw new Error('Unable to retrieve persona data.');
  }

  return {
    id: dto.id,
    name: dto.name,
    schemaVersion: dto.schemaVersion,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    data: dto.data,
  };
}

export function personaEntityToInputDto(
  entity: PersonaEntity
): PersonaInputDto {
  return {
    name: entity.name,
    schemaVersion: entity.schemaVersion,
    data: entity.data,
  };
}
